import GlobalConfig from '../GlobalConfig'
import { Constants, I18n } from 'galarm-config'
import { Box, Button, Layer, Menu, Text } from 'grommet'
import React, { useContext, useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import UserContext from './UserContext'
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { getAuth, signOut } from 'firebase/auth'
import Feedback from './Feedback'
import { Close } from 'grommet-icons'
import DocChatbot from './DocChatbot'

const GalarmEnterpriseHeader = () => {
  const { user, userRoles } = useContext(UserContext)

  const isIncidentCoordinator = userRoles.includes(
    Constants.ENTERPRISE_USER_ROLES.INCIDENT_COORDINATOR
  )

  const isViewer = userRoles.includes(Constants.ENTERPRISE_USER_ROLES.VIEWER)

  const hasRestrictedMenu = isIncidentCoordinator || isViewer

  const [showContactSupport, setShowContactSupport] = useState(false)
  const hideContactSupport = () => setShowContactSupport(false)
  const displayContactSupport = () => setShowContactSupport(true)

  const [isChatbotOpen, setIsChatbotOpen] = useState(false)
  const hideChatbot = () => setIsChatbotOpen(false)
  const displayChatbot = () => setIsChatbotOpen(true)

  const [showHelpPopup, setShowHelpPopup] = useState(false)
  const hideHelpPopup = () => setShowHelpPopup(false)
  const displayHelpPopup = () => setShowHelpPopup(true)

  const [numOrganizations, setNumOrganizations] = useState(0)
  const showSwitchOrganization = numOrganizations > 1

  const navigate = useNavigate()
  const location = useLocation()

  // Determine how many organizations the user belongs to, so we only show
  // the switch organization option when there is more than one to switch to
  useEffect(() => {
    const checkNumOrganizations = async () => {
      if (user) {
        try {
          const userDoc = await getDoc(
            doc(GlobalConfig.firestoreDb, 'enterpriseUsers', user.uid)
          )
          setNumOrganizations(userDoc.data()?.organizations?.length || 0)
        } catch (error) {
          console.error('Error checking number of organizations:', error)
        }
      }
    }
    checkNumOrganizations()
  }, [user])

  // Show help popup after a delay
  useEffect(() => {
    const checkHelpPopup = async () => {
      if (user) {
        try {
          const userDoc = await getDoc(
            doc(GlobalConfig.firestoreDb, 'enterpriseUsers', user.uid)
          )
          const hasSeenHelpPopup = userDoc.data()?.hasSeenHelpPopup
          if (!hasSeenHelpPopup) {
            const timer = setTimeout(() => {
              displayHelpPopup()
            }, 5000) // Show after 5 seconds
            return () => clearTimeout(timer)
          }
        } catch (error) {
          console.error('Error checking help popup status:', error)
        }
      }
    }
    checkHelpPopup()
  }, [user])

  const handleDismissHelpPopup = async () => {
    hideHelpPopup()
    if (user) {
      try {
        await updateDoc(
          doc(GlobalConfig.firestoreDb, 'enterpriseUsers', user.uid),
          { hasSeenHelpPopup: true }
        )
      } catch (error) {
        console.error('Error updating help popup status:', error)
      }
    }
  }

  const handleOpenChatbot = async () => {
    hideHelpPopup()
    displayChatbot()
    if (user) {
      try {
        await updateDoc(
          doc(GlobalConfig.firestoreDb, 'enterpriseUsers', user.uid),
          { hasSeenHelpPopup: true }
        )
      } catch (error) {
        console.error('Error updating help popup status:', error)
      }
    }
  }

  const onLogOut = async () => {
    const auth = getAuth()
    await signOut(auth)
    window.localStorage.removeItem('enterpriseAccountId')
    navigate('/login', { replace: true })
  }

  const onLogin = () => {
    navigate('/login', { replace: true })
  }

  const onChangePassword = async () => {
    navigate('/changePassword')
  }

  const onGoToAccount = () => {
    navigate('/app/account')
  }

  const onSwitchOrganization = () => {
    navigate('/app/chooseorg')
  }

  const onNewOrganization = () => {
    const enterpriseUser = getDoc(
      doc(GlobalConfig.firestoreDb, 'enterpriseUsers', user.uid)
    ).then(userSnapshot => userSnapshot.data())

    // This is due to a Firestore limitation on the size of the array
    if (enterpriseUser?.organizations?.length >= 10) {
      GlobalConfig.showTransientAlert({
        message: I18n.t('maxOrganizationsReached'),
        type: 'error'
      })
    }
    navigate('/app/buy')
    return
  }

  return (
    <Box align="center" justify="end" pad="small" direction="row">
      {!location.pathname.includes('login') &&
        !location.pathname.includes('signup') &&
        !location.pathname.includes('getStarted') &&
        (user ? (
          <Box align="end">
            <Box direction="row" align="center" gap="medium">
              <Link
                style={{
                  color: 'blue',
                  textDecoration: 'none'
                }}
                to="/app"
                rel="noopener noreferrer">
                {I18n.t('home')}
              </Link>
              <Text
                onClick={() => {
                  if (isChatbotOpen) {
                    hideChatbot()
                  } else {
                    displayChatbot()
                  }
                }}
                color="blue"
                style={{ cursor: 'pointer' }}>
                {I18n.t('helpAssistant')}
              </Text>
              <Text
                onClick={displayContactSupport}
                color="blue"
                style={{ cursor: 'pointer' }}>
                {I18n.t('contactSupport')}
              </Text>
              <Menu
                label={I18n.t('welcomeUser', {
                  name: user.displayName || 'User'
                })}
                items={
                  hasRestrictedMenu
                    ? [
                        ...(showSwitchOrganization
                          ? [
                              [
                                {
                                  label: I18n.t('switchOrganization'),
                                  onClick: onSwitchOrganization
                                }
                              ]
                            ]
                          : []),
                        [
                          {
                            label: I18n.t('changePassword'),
                            onClick: onChangePassword
                          },
                          { label: I18n.t('logOut'), onClick: onLogOut }
                        ]
                      ]
                    : [
                        [
                          {
                            label: I18n.t('myAccount'),
                            onClick: onGoToAccount
                          },
                          ...(showSwitchOrganization
                            ? [
                                {
                                  label: I18n.t('switchOrganization'),
                                  onClick: onSwitchOrganization
                                }
                              ]
                            : []),
                          {
                            label: I18n.t('newOrganization'),
                            onClick: onNewOrganization
                          }
                        ],
                        [
                          {
                            label: I18n.t('changePassword'),
                            onClick: onChangePassword
                          },
                          { label: I18n.t('logOut'), onClick: onLogOut }
                        ]
                      ]
                }
              />
            </Box>
          </Box>
        ) : (
          <Button
            primary
            label={I18n.t('login')}
            style={{ color: 'white' }}
            onClick={onLogin}
          />
        ))}
      {showContactSupport && (
        <Layer
          background="lightTint"
          onEsc={hideContactSupport}
          onClickOutside={hideContactSupport}
          margin="medium">
          <Box margin="medium">
            <Feedback onClose={hideContactSupport} />
            <Box style={{ position: 'absolute', top: 20, right: 20 }}>
              <Close onClick={hideContactSupport} />
            </Box>
          </Box>
        </Layer>
      )}
      {showHelpPopup && (
        <Layer
          background="lightTint"
          onEsc={hideHelpPopup}
          onClickOutside={hideHelpPopup}>
          <Box pad="medium" round="small" width="medium" gap="small">
            <Text size="large" weight="bold">
              {I18n.t('needHelp')}
            </Text>
            <Text>{I18n.t('helpPopupMessage')}</Text>
            <Box
              direction="row"
              gap="small"
              justify="end"
              margin={{ top: 'small' }}>
              <Button
                label={I18n.t('close')}
                onClick={handleDismissHelpPopup}
                secondary
              />
              <Button
                label={I18n.t('askTheHelpAssistant')}
                onClick={handleOpenChatbot}
                primary
              />
            </Box>
          </Box>
        </Layer>
      )}
      <DocChatbot isOpen={isChatbotOpen} onClose={hideChatbot} />
    </Box>
  )
}

export default GalarmEnterpriseHeader
